/**
 * 공급 흐름도(flo) SVG 렌더러 — floModel 이 배치한 모델을 색과 모양으로 그린다.
 * 배치(칸 번호·경유점·포트 순번·트랙)는 floGeometry.js 가 이미 확정해 보낸다.
 * 이 컴포넌트는 상태가 없다 — 선택(강조)은 부모가 쥐고 selection/onSelect 로 넘겨받는다.
 * 중립 leaf — pages/** 를 import 하지 않는다.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { textWidth, truncateToWidth } from "@plannel/utils/text-measure-util";
import ProcIcon from "@plannel/components/flo/FloProcIcon";
import {
  BOX_W, TRI_W, TRI_H, PROC_BOX_W, PROC_BOX_H, RES_OVAL_W, RES_OVAL_H, LABEL_MAX_W,
} from "@plannel/components/flo/floGeometry";
import { lineage } from "@plannel/components/flo/floLineage";
import { FLO_COLORS, EDGE_COLOR } from "@plannel/components/flo/floTokens";

// 계보 강조 — 선택 밖 노드·엣지·칩·배지는 옅어진다(부드러운 전환을 선호해 450ms 로 둔다).
const DIM_OPACITY = 0.2;
const DIM_TRANSITION = "opacity 450ms ease";
// 엣지를 클릭으로 잡기 쉽게 까는 투명한 넓은 히트 경로의 굵기.
const EDGE_HIT_WIDTH = 10;

/** 옅게 칠할지에 따른 스타일 — 클릭 가능(clickable)하지 않으면 커서를 바꾸지 않는다. */
function dimStyle(dim, clickable) {
  return { opacity: dim ? DIM_OPACITY : 1, transition: DIM_TRANSITION, cursor: clickable ? "pointer" : undefined };
}

/** 화살촉 marker — id 를 markerBase 로 접두해 한 페이지에 여러 그림이 있어도 안 겹친다. */
function ArrowDefs({ kinds, markerBase }) {
  const arrow = (id, color) => (
    <marker key={id} id={id} viewBox="0 0 10 10" refX="8.5" refY="5"
            markerWidth="7" markerHeight="7" orient="auto">
      <path d="M0,0 L10,5 L0,10 z" fill={color} />
    </marker>
  );
  return (
    <defs>
      {kinds.map((k) => arrow(`${markerBase}-arrow-${k}`, EDGE_COLOR[k] || FLO_COLORS.backFlow))}
      {arrow(`${markerBase}-arrow-back`, FLO_COLORS.backFlow)}
    </defs>
  );
}

/** 점그리드 패턴 — markerBase 로 유일한 id 다. */
function DotGridDefs({ markerBase }) {
  return (
    <defs>
      <pattern id={`${markerBase}-dots`} width="18" height="18" patternUnits="userSpaceOnUse">
        <circle cx="9" cy="9" r="1" fill={FLO_COLORS.dot} />
      </pattern>
    </defs>
  );
}

// 그림자 — 같은 도형을 아래로 조금 밀어 옅은 검정으로 먼저 깐다(흐림 없음).
// SVG 흐림 필터는 도형마다 흐림 연산을 해, 그림 전체가 보이는 축소 상태에서 이동·확대 프레임을 크게 늦춘다.
const SHADOW = { tri: [2, 0.16], box: [2, 0.1], pill: [1, 0.22] };

/** 그림자 도형에 줄 속성 — 원래 도형과 같은 기하 속성에 펼쳐 붙인다. */
function shadowProps(kind) {
  const [dy, opacity] = SHADOW[kind];
  return {
    "data-flo-shadow": "", fill: "#000", fillOpacity: opacity, stroke: "none",
    transform: `translate(0 ${dy})`, pointerEvents: "none",
  };
}

/** 레인 헤어라인 — 각 레인의 위쪽 경계, 그리고 맨 아래 경계에 1px 선을 긋는다. */
function LaneHairlines({ lanes, width }) {
  return (
    <g>
      {lanes.map((lane) => (
        <line key={lane.k} data-flo-lane={lane.k} x1={0} y1={lane.top} x2={width} y2={lane.top}
              stroke={FLO_COLORS.laneBorder} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      ))}
      {lanes.length > 0 && (
        <line data-flo-lane-bottom x1={0} x2={width}
              y1={lanes[lanes.length - 1].top + lanes[lanes.length - 1].height}
              y2={lanes[lanes.length - 1].top + lanes[lanes.length - 1].height}
              stroke={FLO_COLORS.laneBorder} strokeWidth={1} vectorEffect="non-scaling-stroke" />
      )}
    </g>
  );
}

/** 열 경계 안내선 — 인접한 두 열 중심의 중점에, 옅은 세로선을 그림 전체 높이로 긋는다. */
function ColumnGuides({ geometry }) {
  const { colX0, maxCol, height } = geometry;
  if (!maxCol || maxCol < 1) return null;
  const centers = [];
  for (let c = 0; c <= maxCol; c += 1) centers.push(colX0(c) + BOX_W / 2);
  return (
    <g>
      {centers.slice(0, -1).map((cx, i) => {
        const x = (cx + centers[i + 1]) / 2;
        return (
          <line key={i} data-flo-col-guide={i} x1={x} y1={0} x2={x} y2={height}
                stroke={FLO_COLORS.colGuide} strokeWidth={1} vectorEffect="non-scaling-stroke" />
        );
      })}
    </g>
  );
}

/** 이름 두 줄 라벨(흰 반투명 칩 배경) — 모든 도형이 공유한다. y1·y2 는 각 줄의 텍스트 기준선. */
function LabelLines({ cx, y1, y2, line1, line2, fonts, mini }) {
  const l1 = truncateToWidth(line1, LABEL_MAX_W, fonts.labelFont);
  const l2 = !mini && line2 ? truncateToWidth(line2, LABEL_MAX_W, fonts.codeFont) : "";
  const w1 = textWidth(l1, fonts.labelFont) + 6;
  const w2 = l2 ? textWidth(l2, fonts.codeFont) + 6 : 0;
  return (
    <g>
      {l1 && (
        <>
          <rect x={cx - w1 / 2} y={y1 - 11} width={w1} height={14} rx={3} fill="rgba(255,255,255,0.85)" />
          <text x={cx} y={y1} textAnchor="middle" style={{ font: fonts.labelFont, fontWeight: 600, fill: FLO_COLORS.text1 }}>{l1}</text>
        </>
      )}
      {l2 && (
        <>
          <rect x={cx - w2 / 2} y={y2 - 10} width={w2} height={13} rx={3} fill="rgba(255,255,255,0.85)" />
          <text x={cx} y={y2} textAnchor="middle" style={{ font: fonts.codeFont, fill: FLO_COLORS.text2 }}>{l2}</text>
        </>
      )}
    </g>
  );
}

/** 필(pill) 배지 — 높이16 rx8 1px 테두리, 글자는 fonts.badgeFont(굵게) 다. */
function PillBadge({ x, y, text, fill, border, fonts, title }) {
  const font = fonts.badgeFont;
  const w = Math.max(16, textWidth(text, font) + 8);
  const bx = x - w / 2;
  return (
    <g>
      <rect x={bx} y={y - 8} width={w} height={16} rx={8} {...shadowProps("pill")} />
      <rect x={bx} y={y - 8} width={w} height={16} rx={8} fill={fill} stroke={border} strokeWidth={1} />
      <text x={bx + w / 2} y={y + 4} textAnchor="middle" style={{ font, fill: "#000" }}>{text}</text>
      {title && <title>{title}</title>}
    </g>
  );
}

/**
 * 노드 둘째 줄 — 운송(move) 노드의 sub 는 경로 문구라 늘 보인다.
 * 그 외 노드는 위치(레인) 이름 성격이라, locationLabels 를 켰을 때만 sub(폴백은 레인 이름)를 보인다.
 */
function line2Of(node, lane, locationLabels) {
  if (node.t === "move") return node.sub || "";
  return locationLabels ? (node.sub || lane?.label || lane?.code || "") : "";
}

/** 재고/집약 노드 — 꼭짓점 위 삼각형(그림자) + 라벨 두 줄. 공급 상태에 따라 채움·테두리가 갈린다. */
function InvNode({ g, mini, dir, t, fonts, dim, clickable, onNodeClick, locationLabels }) {
  const node = g.node;
  const isAgg = node.t === "agg";
  const root = !!node.root;
  const pur = !!node.pur || g.lane.type === "VENDOR";
  const supNone = dir === "up" && node.supply === "none";
  const supUnknown = node.supply === "unknown";

  let fill = FLO_COLORS.invFill;
  let stroke = FLO_COLORS.invStroke;
  let strokeWidth = 1;
  let dash;
  if (pur) { fill = FLO_COLORS.invPurFill; stroke = FLO_COLORS.invPurStroke; strokeWidth = 1.5; }
  if (root) { fill = FLO_COLORS.invRootFill; stroke = FLO_COLORS.invRootStroke; strokeWidth = 2; }
  if (supUnknown) { fill = FLO_COLORS.invUnknownFill; stroke = FLO_COLORS.invUnknownStroke; dash = "3 2"; }
  if (supNone) { stroke = FLO_COLORS.invGapStroke; strokeWidth = 1.5; dash = "3 2"; }
  if (isAgg) { fill = FLO_COLORS.invAggFill; stroke = FLO_COLORS.invAggStroke; strokeWidth = 1; dash = undefined; }

  const pts = `${g.cx},${g.yA - TRI_H / 2} ${g.cx + TRI_W / 2},${g.yA + TRI_H / 2} ${g.cx - TRI_W / 2},${g.yA + TRI_H / 2}`;
  const gapText = (node.g || []).join(" · ");
  const titleLines = [
    `${node.label}${node.code ? ` · ${node.code}` : ""}`,
    `${g.lane.label} · ${g.lane.code}`,
    ...(node.tip || []),
    ...(node.g || []),
    isAgg && node.items ? node.items.join(", ") : "",
  ].filter(Boolean);

  const line1 = isAgg ? t("floViewFoldedCount", { n: node.n }) : node.label;

  return (
    <g data-flo-node={node.k} data-dim={dim ? "1" : undefined} style={dimStyle(dim, clickable)}
       onClick={clickable ? onNodeClick : undefined}>
      <polygon points={pts} {...shadowProps("tri")} />
      <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={strokeWidth} strokeDasharray={dash} />
      <LabelLines cx={g.cx} y1={g.triBottom + 14} y2={g.triBottom + 28}
                  line1={line1} line2={line2Of(node, g.lane, locationLabels)} fonts={fonts} mini={mini} />
      {node.g && node.g.length > 0 && (
        <PillBadge x={g.cx + TRI_W / 2 - 4} y={g.triTop} text="!" fill={FLO_COLORS.invGapFill}
                   border={FLO_COLORS.invGapStroke} fonts={fonts} title={gapText} />
      )}
      {node.x && (
        <PillBadge x={dir === "up" ? g.x0 - 4 : g.x1 + 4} y={g.yA} text="…" fill={FLO_COLORS.moreFill}
                   border={FLO_COLORS.moreStroke} fonts={fonts} />
      )}
      <title>{titleLines.join("\n")}</title>
    </g>
  );
}

/** 공정 노드 — 둥근 상자(운송은 점선) + 아이콘. 설비 딸린 생산은 위에 오벌 위성 + 연결선을 낸다. */
function ProcNode({ g, mini, dir, fonts, dim, clickable, onNodeClick, locationLabels }) {
  const node = g.node;
  const kind = node.t;
  let fill = FLO_COLORS.makeFill;
  let stroke = FLO_COLORS.makeStroke;
  let strokeWidth = 2;
  let dash;
  if (kind === "kit") { fill = FLO_COLORS.kitFill; stroke = FLO_COLORS.kitStroke; }
  if (kind === "move") { fill = FLO_COLORS.moveFill; stroke = FLO_COLORS.moveStroke; strokeWidth = 1.5; dash = "4 3"; }

  const iconSize = 18;
  const tipLines = [...(node.tip || []), `${g.lane.label} · ${g.lane.code}`];
  if (node.res) tipLines.push(...(node.res.tip || []));

  return (
    <g data-flo-node={node.k} data-dim={dim ? "1" : undefined} style={dimStyle(dim, clickable)}
       onClick={clickable ? onNodeClick : undefined}>
      {g.shape === "res" && (
        // 연결선을 라벨보다 먼저 그려 라벨의 흰 칩이 위를 덮는다.
        <line x1={g.cx} y1={g.ovalBottom} x2={g.cx} y2={g.boxTop} stroke={FLO_COLORS.resStroke} strokeWidth={1} />
      )}
      <rect x={g.cx - PROC_BOX_W / 2} y={g.boxTop} width={PROC_BOX_W} height={PROC_BOX_H} rx={8}
            {...shadowProps("box")} />
      <rect x={g.cx - PROC_BOX_W / 2} y={g.boxTop} width={PROC_BOX_W} height={PROC_BOX_H} rx={8}
            fill={fill} stroke={stroke} strokeWidth={strokeWidth} strokeDasharray={dash} />
      <ProcIcon kind={kind} x={g.cx - iconSize / 2} y={g.yA - iconSize / 2} size={iconSize} color={stroke} />
      <LabelLines cx={g.cx} y1={g.boxBottom + 14} y2={g.boxBottom + 28}
                  line1={node.label} line2={line2Of(node, g.lane, locationLabels)} fonts={fonts} mini={mini} />
      {g.shape === "res" && (
        <>
          <ellipse cx={g.cx} cy={(g.ovalTop + g.ovalBottom) / 2} rx={RES_OVAL_W / 2} ry={RES_OVAL_H / 2}
                   {...shadowProps("box")} />
          <ellipse cx={g.cx} cy={(g.ovalTop + g.ovalBottom) / 2} rx={RES_OVAL_W / 2} ry={RES_OVAL_H / 2}
                   fill={FLO_COLORS.resFill} stroke={FLO_COLORS.resStroke} strokeWidth={2} />
          {!mini && (
            <LabelLines cx={g.cx} y1={g.ovalBottom + 11} y2={0} line1={node.res.label} line2="" fonts={fonts} mini />
          )}
          {!mini && node.res.n > 1 && (
            <PillBadge x={g.cx + RES_OVAL_W / 2 - 6} y={g.ovalTop} text={`+${node.res.n - 1}`}
                       fill="#ffffff" border="#616161" fonts={fonts} />
          )}
        </>
      )}
      {node.x && (
        <PillBadge x={dir === "up" ? g.x0 - 4 : g.x1 + 4} y={g.yA} text="…" fill={FLO_COLORS.moreFill}
                   border={FLO_COLORS.moreStroke} fonts={fonts} />
      )}
      <title>{tipLines.filter(Boolean).join("\n")}</title>
    </g>
  );
}

/** 소요량(q)·비율(r) 알약 칩 — 필 배지와 같은 스타일, 색만 갈린다. */
function Chip({ e, fonts }) {
  const c = e.chip;
  if (!c || c.hidden) return null;
  const isRatio = e.chipType === "r";
  const fill = isRatio ? FLO_COLORS.chipRatioFill : FLO_COLORS.chipQtyFill;
  const border = isRatio ? FLO_COLORS.chipRatioBorder : FLO_COLORS.chipQtyBorder;
  return (
    <g data-flo-chip={e.key}>
      <rect x={c.x - c.w / 2} y={c.y - c.h / 2} width={c.w} height={c.h} rx={c.h / 2} {...shadowProps("pill")} />
      <rect x={c.x - c.w / 2} y={c.y - c.h / 2} width={c.w} height={c.h} rx={c.h / 2}
            fill={fill} stroke={border} strokeWidth={1} />
      <text x={c.x} y={c.y + 4} textAnchor="middle" style={{ font: fonts.codeFont, fontWeight: 700, fill: "#000" }}>{c.text}</text>
    </g>
  );
}

/**
 * 공급 흐름도 SVG 본체 — 상태가 없다.
 * selection·onSelect 는 부모가 쥔 강조 상태를 그대로 받아 쓰는 제어(controlled) 계약이다.
 * onSelect 가 없거나 mini 면 클릭 핸들러 자체를 달지 않고 커서도 바꾸지 않는다.
 */
export default function FloSvg({
  model, geometry, edges, fonts, mini, dotGrid, markerBase, selection, onSelect, style, viewBox, width, height,
  locationLabels = false,
}) {
  const { t } = useTranslation();
  const dir = model.dir;
  const kinds = useMemo(() => Array.from(new Set((edges || []).map((e) => e.edge.kind))), [edges]);
  const clickable = !!onSelect && !mini;
  const lin = useMemo(() => lineage(model, selection), [model, selection]);
  const nodeDim = (key) => !!lin && !lin.nodes.has(key);
  const edgeDim = (ei) => !!lin && !lin.edges.has(ei);

  // 노드/엣지 클릭은 각자 stopPropagation 한다 — 그래야 FloCanvas 의 배경 클릭(선택 해제)이 뒤따라 겹쳐 일어나지 않는다.
  const nodeClick = (key) => (event) => {
    event.stopPropagation();
    const next = selection?.type === "node" && selection.key === key ? null : { type: "node", key };
    onSelect(next);
  };
  const edgeClick = (index) => (event) => {
    event.stopPropagation();
    const next = selection?.type === "edge" && selection.index === index ? null : { type: "edge", index };
    onSelect(next);
  };

  return (
    <svg viewBox={viewBox} width={width} height={height}
         style={{ userSelect: "none", WebkitUserSelect: "none", ...style }}>
      <ArrowDefs kinds={kinds} markerBase={markerBase} />
      {dotGrid && <DotGridDefs markerBase={markerBase} />}
      <rect x={0} y={0} width={geometry.width} height={geometry.height} fill={FLO_COLORS.canvasBg} />
      {dotGrid && <rect x={0} y={0} width={geometry.width} height={geometry.height} fill={`url(#${markerBase}-dots)`} />}
      <LaneHairlines lanes={geometry.lanes} width={geometry.width} />
      <ColumnGuides geometry={geometry} />
      <g>
        {(edges || []).filter((e) => e.back).map((e) => {
          const dim = edgeDim(e.ei);
          return (
            <g key={e.key} data-dim={dim ? "1" : undefined}
               style={{ opacity: dim ? DIM_OPACITY : 0.65, transition: DIM_TRANSITION,
                       cursor: clickable ? "pointer" : undefined }}
               onClick={clickable ? edgeClick(e.ei) : undefined}>
              <path data-flo-back-edge={`${e.edge.s}-${e.edge.t}`} d={e.d} fill="none"
                    stroke={FLO_COLORS.backFlow} strokeWidth={1.5} strokeDasharray="2 3"
                    markerEnd={`url(#${markerBase}-arrow-back)`}>
                <title>{e.chip?.hidden ? e.chip.text : ""}</title>
              </path>
              <path data-flo-edge-hit={e.ei} d={e.d} fill="none" stroke="transparent"
                    strokeWidth={EDGE_HIT_WIDTH} style={{ pointerEvents: clickable ? "stroke" : "none" }} />
            </g>
          );
        })}
      </g>
      <g>
        {(edges || []).filter((e) => !e.back).map((e) => {
          const dim = edgeDim(e.ei);
          return (
            <g key={e.key} data-flo-edge={`${e.edge.s}-${e.edge.t}`} data-flo-edge-kind={e.edge.kind}
               data-dim={dim ? "1" : undefined} style={dimStyle(dim, clickable)}
               onClick={clickable ? edgeClick(e.ei) : undefined}>
              <path d={e.d} fill="none" stroke={EDGE_COLOR[e.edge.kind] || FLO_COLORS.backFlow} strokeWidth={1.5}
                    strokeDasharray={e.edge.kind === "move" ? "7 4" : undefined}
                    markerEnd={`url(#${markerBase}-arrow-${e.edge.kind})`} />
              <path data-flo-edge-hit={e.ei} d={e.d} fill="none" stroke="transparent"
                    strokeWidth={EDGE_HIT_WIDTH} style={{ pointerEvents: clickable ? "stroke" : "none" }} />
              {e.chip?.hidden && <title>{e.chip.text}</title>}
              {!mini && <Chip e={e} fonts={fonts} />}
            </g>
          );
        })}
      </g>
      <g>
        {(model.nodes || []).map((n) => {
          const g = geometry.nodes[n.k];
          if (!g) return null;
          const Comp = g.shape === "inv" ? InvNode : ProcNode;
          return (
            <Comp key={n.k} g={g} mini={mini} dir={dir} t={t} fonts={fonts}
                  dim={nodeDim(n.k)} clickable={clickable} onNodeClick={clickable ? nodeClick(n.k) : undefined}
                  locationLabels={locationLabels} />
          );
        })}
      </g>
    </svg>
  );
}
